import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { MessageCircle, X, Plus, ChevronRight, Loader2, Star, CheckCircle2, Megaphone, ArrowLeft, Send, Bell, Search, Paperclip, Pencil, Trash2, FileText, Play } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { ChatWindow } from "./ChatWindow";
import { Avatar } from "./Avatar";
import { RichTextEditor } from "../RichTextEditor";
import { useToast } from "../../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN = () => localStorage.getItem("token") || "";
const avisoArquivoUrl = (path) => `${API}/chat/aviso-arquivo?path=${encodeURIComponent(path)}&auth=${encodeURIComponent(TOKEN())}`;
const isImg = (mime) => (mime || "").startsWith("image/");
const isAudio = (mime) => (mime || "").startsWith("audio/");

const PERFIS_AVISO = [
  { v: "cuidador", l: "Cuidadores" }, { v: "tecnico_enfermagem", l: "Técnicos/Aux." },
  { v: "enfermeiro", l: "Enfermeiros" }, { v: "medico", l: "Médicos" },
  { v: "nutricionista", l: "Nutricionistas" }, { v: "fisioterapeuta", l: "Fisioterapeutas" },
];
const dataHora = (iso) => { try { const d = new Date(iso); if (isNaN(d.getTime())) return ""; return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

// Carrega o anexo de aviso via fetch autenticado (Bearer) e o exibe como blob — robusto em Preview e Produção.
const AvisoImagem = ({ path, testid }) => {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let url; let alive = true;
    axios.get(`${API}/chat/aviso-arquivo`, { params: { path }, responseType: "blob" })
      .then((r) => { if (!alive) return; url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (!src) return <div className="mt-2 h-24 w-full rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center text-xs text-slate-400" data-testid={testid}>Carregando anexo…</div>;
  return <img src={src} alt="anexo" className="mt-2 max-h-40 rounded-lg border border-slate-100" data-testid={testid} />;
};
const AvisoAudio = ({ path, testid }) => {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let url; let alive = true;
    axios.get(`${API}/chat/aviso-arquivo`, { params: { path }, responseType: "blob" })
      .then((r) => { if (!alive) return; url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (!src) return null;
  return <audio controls src={src} className="mt-2 w-full" data-testid={testid} />;
};

export const FloatingChat = () => {
  const { auth, chatOpen, chatTab, openChat, closeChat, naoLidasChat, carregarNaoLidasChat } = useApp();
  const { toast } = useToast();
  const perfil = auth?.perfil;
  const STAFF = ["administrador", "enfermeiro", "cuidador", "tecnico_enfermagem", "medico", "nutricionista", "fisioterapeuta"];
  const habilitado = perfil === "familia" || STAFF.includes(perfil);
  const podeAvisar = ["administrador", "enfermeiro"].includes(perfil);
  const meLado = perfil === "familia" ? "familia" : "equipe";

  const aberto = chatOpen;
  const [tab, setTab] = useState("conversas");
  const naoLidas = naoLidasChat;
  const carregarNaoLidas = carregarNaoLidasChat;
  const [conversas, setConversas] = useState([]);
  const [sel, setSel] = useState(null);
  const [novaAberta, setNovaAberta] = useState(false);
  const [contatos, setContatos] = useState({ funcionarios: [], familias: [] });
  const [avisos, setAvisos] = useState([]);
  const [compor, setCompor] = useState(false);
  const [form, setForm] = useState({ id: null, titulo: "", texto: "", perfis: [], familias: false, anexoPath: "", anexoNome: "", anexoMime: "" });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [busca, setBusca] = useState("");
  const [buscaC, setBuscaC] = useState("");
  const [avisoDet, setAvisoDet] = useState(null);

  const carregarConversas = useCallback(async () => {
    if (!habilitado) return;
    try {
      const url = perfil === "familia" ? `${API}/chat/conversas` : `${API}/chat/conversas?mine=1`;
      const r = await axios.get(url);
      setConversas(r.data || []);
    } catch (e) { /* ignore */ }
  }, [habilitado, perfil]);

  const carregarAvisos = useCallback(async () => {
    if (!habilitado) return;
    try { const r = await axios.get(`${API}/chat/avisos`); setAvisos(r.data || []); } catch (e) { /* ignore */ }
  }, [habilitado]);

  useEffect(() => { if (chatOpen) setTab(chatTab || "conversas"); }, [chatOpen, chatTab]);
  useEffect(() => {
    if (!aberto) return;
    carregarConversas(); carregarAvisos();
    const t = setInterval(() => { carregarConversas(); carregarAvisos(); }, 8000);
    return () => clearInterval(t);
  }, [aberto, carregarConversas, carregarAvisos]);

  const abrirNova = async () => {
    setNovaAberta(true);
    try { const r = await axios.get(`${API}/chat/contatos`); setContatos(r.data || { funcionarios: [], familias: [] }); } catch (e) { /* ignore */ }
  };

  const iniciarCom = async (s) => {
    try {
      const body = perfil === "familia" ? { staffId: s.id } : { tipo: s.tipo, alvoId: s.id };
      const r = await axios.post(`${API}/chat/conversas`, body);
      setNovaAberta(false); setSel(r.data); carregarConversas();
    } catch (e) { toast({ title: e?.response?.data?.detail || "Não foi possível iniciar.", variant: "destructive" }); }
  };

  const fixar = async (c, e) => { e.stopPropagation(); try { await axios.post(`${API}/chat/conversas/${c.id}/fixar`); carregarConversas(); } catch (err) {} };
  const resolver = async (c, e) => { e.stopPropagation(); try { await axios.post(`${API}/chat/conversas/${c.id}/resolver`); carregarConversas(); } catch (err) {} };

  const abrirAviso = async (a) => {
    if (a.souAutor) { verLeituras(a); return; }
    if (!a.lido) { try { await axios.post(`${API}/chat/avisos/${a.id}/ler`); } catch (e) {} }
    setAvisos((xs) => xs.map((x) => x.id === a.id ? { ...x, lido: true } : x));
    carregarNaoLidas();
  };

  const verLeituras = async (a) => {
    try { const r = await axios.get(`${API}/chat/avisos/${a.id}/leituras`); setAvisoDet({ aviso: a, ...r.data }); }
    catch (e) { toast({ title: "Não foi possível carregar as leituras.", variant: "destructive" }); }
  };

  const resetForm = () => setForm({ id: null, titulo: "", texto: "", perfis: [], familias: false, anexoPath: "", anexoNome: "", anexoMime: "" });

  const anexarArquivo = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await axios.post(`${API}/chat/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, anexoPath: r.data.path, anexoNome: r.data.nome, anexoMime: r.data.mime }));
    } catch (err) { toast({ title: err?.response?.data?.detail || "Falha ao anexar.", variant: "destructive" }); }
    finally { setUploading(false); }
  };

  const enviarAviso = async () => {
    const semTexto = !form.texto || form.texto.replace(/<[^>]*>/g, "").trim() === "";
    if (semTexto && !form.anexoPath) { toast({ title: "Escreva o aviso ou anexe um arquivo.", variant: "destructive" }); return; }
    if (form.perfis.length === 0 && !form.familias) { toast({ title: "Selecione ao menos um destinatário.", variant: "destructive" }); return; }
    const payload = { titulo: form.titulo, texto: form.texto, alvoPerfis: form.perfis, alvoFamilias: form.familias, anexoPath: form.anexoPath, anexoNome: form.anexoNome, anexoMime: form.anexoMime };
    try {
      if (form.id) await axios.put(`${API}/chat/avisos/${form.id}`, payload);
      else await axios.post(`${API}/chat/avisos`, payload);
      resetForm(); setCompor(false); carregarAvisos();
      toast({ title: form.id ? "Aviso atualizado." : "Aviso enviado à equipe." });
    } catch (e) { toast({ title: e?.response?.data?.detail || "Falha ao enviar.", variant: "destructive" }); }
  };

  const editarAvisoStart = (a, e) => {
    e && e.stopPropagation();
    setForm({ id: a.id, titulo: a.titulo || "", texto: a.texto || "", perfis: a.alvoPerfis || [], familias: !!a.alvoFamilias, anexoPath: a.anexoPath || "", anexoNome: a.anexoNome || "", anexoMime: a.anexoMime || "" });
    setAvisoDet(null); setCompor(true);
  };

  const excluirAvisoAction = async (a, e) => {
    e && e.stopPropagation();
    if (!window.confirm("Excluir este aviso?")) return;
    try { await axios.delete(`${API}/chat/avisos/${a.id}`); carregarAvisos(); carregarNaoLidas(); toast({ title: "Aviso excluído." }); }
    catch (err) { toast({ title: err?.response?.data?.detail || "Não foi possível excluir.", variant: "destructive" }); }
  };

  if (!habilitado) return null;
  const refresh = () => { carregarNaoLidas(); carregarConversas(); };
  const togglePerfil = (v) => setForm((f) => ({ ...f, perfis: f.perfis.includes(v) ? f.perfis.filter((x) => x !== v) : [...f.perfis, v] }));

  return (
    <>
      {!aberto && (
        <button onClick={() => openChat(tab)} data-testid="chat-bubble"
          className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-xl flex items-center justify-center transition-transform hover:scale-105">
          <MessageCircle className="w-6 h-6" />
          {naoLidas.total > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1.5 flex items-center justify-center text-[11px] font-bold text-white bg-red-500 rounded-full border-2 border-white" data-testid="chat-bubble-badge">{naoLidas.total}</span>
          )}
        </button>
      )}

      {aberto && (
        <div className="fixed bottom-5 right-5 z-50 w-[92vw] max-w-[390px] h-[72vh] max-h-[580px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" data-testid="chat-panel">
          <div className="flex items-center justify-between px-4 py-3 bg-blue-600 text-white">
            <div className="flex items-center gap-2 font-semibold text-sm"><MessageCircle className="w-4 h-4" /> Mensagens</div>
            <button onClick={() => { closeChat(); setSel(null); setNovaAberta(false); setCompor(false); }} className="p-1 rounded-md hover:bg-white/20" data-testid="chat-fechar"><X className="w-4 h-4" /></button>
          </div>

          {!sel && (
            <div className="flex border-b border-slate-100 text-sm">
              <button onClick={() => setTab("conversas")} data-testid="tab-conversas" className={`flex-1 py-2.5 font-medium ${tab === "conversas" ? "text-blue-700 border-b-2 border-blue-600" : "text-slate-500"}`}>Conversas</button>
              <button onClick={() => setTab("avisos")} data-testid="tab-avisos" className={`flex-1 py-2.5 font-medium flex items-center justify-center gap-1 ${tab === "avisos" ? "text-blue-700 border-b-2 border-blue-600" : "text-slate-500"}`}>
                Avisos {naoLidas.avisos > 0 && <span className="text-[10px] font-bold text-white bg-red-500 rounded-full px-1.5">{naoLidas.avisos}</span>}
              </button>
            </div>
          )}

          <div className="flex-1 min-h-0 flex flex-col">
            {sel ? (
              <ChatWindow conversa={sel} meLado={meLado} onBack={() => { setSel(null); refresh(); }} onChanged={refresh} embedded />
            ) : tab === "avisos" ? (
              avisoDet ? (
                <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="aviso-leituras">
                  <button onClick={() => setAvisoDet(null)} className="text-blue-600 text-xs flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Voltar</button>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{avisoDet.aviso.titulo || "Aviso"}</p>
                    <p className="text-xs text-slate-500">{avisoDet.lidos} de {avisoDet.total} já leram</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-emerald-600 uppercase mb-1">Leram ({avisoDet.listaLidos.length})</p>
                    {avisoDet.listaLidos.length === 0 && <p className="text-xs text-slate-400">Ninguém ainda.</p>}
                    {avisoDet.listaLidos.map((u) => (
                      <div key={u.id} className="flex items-center gap-2 py-1.5"><Avatar nome={u.nome} foto={u.foto} size={28} /><div className="flex-1 min-w-0"><p className="text-sm text-slate-700 truncate">{u.nome}</p><p className="text-[10px] text-slate-400">{u.label} · {dataHora(u.lidoEm)}</p></div><CheckCircle2 className="w-4 h-4 text-emerald-500" /></div>
                    ))}
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-slate-400 uppercase mb-1">Ainda não leram ({avisoDet.listaNaoLidos.length})</p>
                    {avisoDet.listaNaoLidos.map((u) => (
                      <div key={u.id} className="flex items-center gap-2 py-1.5 opacity-70"><Avatar nome={u.nome} foto={u.foto} size={28} /><div className="flex-1 min-w-0"><p className="text-sm text-slate-700 truncate">{u.nome}</p><p className="text-[10px] text-slate-400">{u.label}</p></div></div>
                    ))}
                  </div>
                </div>
              ) : compor ? (
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  <button onClick={() => { setCompor(false); resetForm(); }} className="text-blue-600 text-xs flex items-center gap-1" data-testid="aviso-voltar"><ArrowLeft className="w-3.5 h-3.5" /> Voltar</button>
                  <input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} placeholder="Título (opcional)" data-testid="aviso-titulo" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500" />
                  <RichTextEditor value={form.texto} onChange={(html) => setForm((f) => ({ ...f, texto: html }))} placeholder="Escreva a orientação para a equipe..." testid="aviso-texto" />
                  <div>
                    <input ref={fileRef} type="file" accept="image/*,application/pdf,audio/*" className="hidden" onChange={anexarArquivo} data-testid="aviso-anexo-input" />
                    <div className="flex items-center gap-2 flex-wrap">
                      <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="aviso-anexar" className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 border border-blue-200 rounded-lg px-3 py-1.5 hover:bg-blue-50 disabled:opacity-50">
                        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />} {uploading ? "Enviando..." : "Anexar foto, PDF ou áudio"}
                      </button>
                      {form.anexoPath && (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 rounded-full px-2 py-1" data-testid="aviso-anexo-chip">
                          <CheckCircle2 className="w-3.5 h-3.5" /> {form.anexoNome || "Anexo"}
                          <button onClick={() => setForm((f) => ({ ...f, anexoPath: "", anexoNome: "", anexoMime: "" }))} className="text-red-500 ml-1"><X className="w-3 h-3" /></button>
                        </span>
                      )}
                    </div>
                    {form.anexoPath && isImg(form.anexoMime) && (
                      <img src={avisoArquivoUrl(form.anexoPath)} alt="anexo" className="mt-2 max-h-32 rounded-lg border border-slate-100" />
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 mb-1.5">Quem recebe:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {PERFIS_AVISO.map((p) => (
                        <button key={p.v} onClick={() => togglePerfil(p.v)} data-testid={`aviso-perfil-${p.v}`}
                          className={`text-xs px-2.5 py-1 rounded-full border ${form.perfis.includes(p.v) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>{p.l}</button>
                      ))}
                      <button onClick={() => setForm({ ...form, familias: !form.familias })} data-testid="aviso-familias"
                        className={`text-xs px-2.5 py-1 rounded-full border ${form.familias ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200"}`}>Familiares</button>
                    </div>
                  </div>
                  <button onClick={enviarAviso} data-testid="aviso-enviar" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium flex items-center justify-center gap-2"><Send className="w-4 h-4" /> {form.id ? "Salvar aviso" : "Enviar aviso"}</button>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto">
                  {podeAvisar && (
                    <button onClick={() => { resetForm(); setCompor(true); }} data-testid="novo-aviso" className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-blue-600 hover:bg-blue-50 border-b border-slate-100"><Megaphone className="w-4 h-4" /> Novo aviso à equipe</button>
                  )}
                  {avisos.length === 0 && <p className="text-sm text-slate-400 text-center p-6">Nenhum aviso.</p>}
                  {avisos.map((a, i) => {
                    const podeEditar = a.souAutor || perfil === "administrador";
                    return (
                    <div key={`${a.id}-${i}`} data-testid={`aviso-${a.id}`} className={`px-4 py-3 border-b border-slate-50 ${a.lido ? "" : "bg-amber-50/60"}`}>
                      <div onClick={() => abrirAviso(a)} className="cursor-pointer">
                        <div className="flex items-center gap-2">
                          <Megaphone className="w-4 h-4 text-amber-500 shrink-0" />
                          <p className="text-sm font-semibold text-slate-800 flex-1 truncate">{a.titulo || "Aviso"}</p>
                          {!a.lido && <span className="w-2 h-2 rounded-full bg-red-500" />}
                        </div>
                        {a.texto && <div className="text-xs text-slate-600 mt-1 aviso-prose" dangerouslySetInnerHTML={{ __html: a.texto }} />}
                        {a.anexoPath && (
                          isImg(a.anexoMime)
                            ? <AvisoImagem path={a.anexoPath} testid={`aviso-img-${a.id}`} />
                            : isAudio(a.anexoMime)
                              ? <AvisoAudio path={a.anexoPath} testid={`aviso-audio-${a.id}`} />
                              : <a href={avisoArquivoUrl(a.anexoPath)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="mt-2 inline-flex items-center gap-1.5 text-xs text-blue-600 border border-blue-200 rounded-lg px-2.5 py-1.5 hover:bg-blue-50" data-testid={`aviso-arquivo-${a.id}`}><FileText className="w-3.5 h-3.5" /> {a.anexoNome || "Abrir anexo"}</a>
                        )}
                        <p className="text-[10px] text-slate-400 mt-1">{a.criadoPorNome} · {a.criadoPorLabel} · {dataHora(a.criadoEm)}{a.souAutor ? " · (você)" : ""}</p>
                      </div>
                      <div className="flex items-center gap-2 mt-1.5">
                        {a.souAutor && (
                          <button onClick={() => verLeituras(a)} className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5" data-testid={`aviso-leram-${a.id}`}>
                            <CheckCircle2 className="w-3 h-3" /> {a.lidosCount ?? 0}/{a.totalCount ?? 0} leram · ver
                          </button>
                        )}
                        {podeEditar && (
                          <div className="ml-auto flex items-center gap-1">
                            <button onClick={(e) => editarAvisoStart(a, e)} className="text-slate-400 hover:text-blue-600 p-1" title="Editar" data-testid={`aviso-editar-${a.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                            <button onClick={(e) => excluirAvisoAction(a, e)} className="text-slate-400 hover:text-red-600 p-1" title="Excluir" data-testid={`aviso-excluir-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              )
            ) : novaAberta ? (
              <div className="flex-1 overflow-y-auto">
                <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2 text-sm text-slate-600">
                  <button onClick={() => setNovaAberta(false)} className="text-blue-600 text-xs" data-testid="nova-voltar">← Voltar</button>
                  <span className="font-medium">Falar com</span>
                </div>
                <div className="px-3 py-2 border-b border-slate-50">
                  <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-slate-100">
                    <Search className="w-3.5 h-3.5 text-slate-400" />
                    <input value={buscaC} onChange={(e) => setBuscaC(e.target.value)} placeholder="Buscar por nome..." data-testid="busca-contato" className="bg-transparent text-sm outline-none w-full text-slate-600" />
                  </div>
                </div>
                {contatos.familias.filter((s) => (s.nome || "").toLowerCase().includes(buscaC.trim().toLowerCase())).length > 0 && <p className="px-4 pt-2 text-[11px] font-semibold text-slate-400 uppercase">Familiares</p>}
                {contatos.familias.filter((s) => (s.nome || "").toLowerCase().includes(buscaC.trim().toLowerCase())).map((s, i) => (
                  <button key={`f-${s.id}-${i}`} onClick={() => iniciarCom(s)} data-testid={`nova-staff-${s.id}`} className="w-full flex items-center gap-2.5 px-4 py-2.5 border-b border-slate-50 hover:bg-slate-50 text-left">
                    <Avatar nome={s.nome} foto={s.foto} size={32} />
                    <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-800 truncate">{s.nome}</p><p className="text-xs text-slate-400">{s.label}</p></div>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </button>
                ))}
                {contatos.funcionarios.filter((s) => (s.nome || "").toLowerCase().includes(buscaC.trim().toLowerCase())).length > 0 && <p className="px-4 pt-2 text-[11px] font-semibold text-slate-400 uppercase">{perfil === "familia" ? "Equipe" : "Funcionários"}</p>}
                {contatos.funcionarios.filter((s) => (s.nome || "").toLowerCase().includes(buscaC.trim().toLowerCase())).map((s, i) => (
                  <button key={`u-${s.id}-${i}`} onClick={() => iniciarCom(s)} data-testid={`nova-staff-${s.id}`} className="w-full flex items-center gap-2.5 px-4 py-2.5 border-b border-slate-50 hover:bg-slate-50 text-left">
                    <Avatar nome={s.nome} foto={s.foto} size={32} />
                    <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-800 truncate">{s.nome}</p><p className="text-xs text-slate-400">{s.label}</p></div>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                <button onClick={abrirNova} data-testid="chat-nova" className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-blue-600 hover:bg-blue-50 border-b border-slate-100"><Plus className="w-4 h-4" /> Nova conversa</button>
                <div className="px-3 py-2 border-b border-slate-50">
                  <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-slate-100">
                    <Search className="w-3.5 h-3.5 text-slate-400" />
                    <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por nome..." data-testid="busca-conversa" className="bg-transparent text-sm outline-none w-full text-slate-600" />
                  </div>
                </div>
                {conversas.filter((c) => (c.nomeExibicao || "").toLowerCase().includes(busca.trim().toLowerCase())).length === 0 && <p className="text-sm text-slate-400 text-center p-6">Nenhuma conversa{busca ? " encontrada" : " ainda"}.</p>}
                {conversas.filter((c) => (c.nomeExibicao || "").toLowerCase().includes(busca.trim().toLowerCase())).map((c, i) => (
                  <div key={`${c.id}-${i}`} onClick={() => setSel(c)} data-testid={`chat-conv-${c.id}`} className={`w-full flex items-center gap-2.5 px-4 py-3 border-b border-slate-50 hover:bg-slate-50 text-left cursor-pointer ${c.fixada ? "bg-amber-50/50" : ""} ${c.resolvida ? "opacity-60" : ""}`}>
                    <Avatar nome={c.nomeExibicao} foto={c.fotoExibicao} size={38} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        {c.fixada && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400 shrink-0" />}
                        {c.resolvida && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
                        <p className="text-sm font-medium text-slate-800 truncate">{c.nomeExibicao}</p>
                      </div>
                      <p className="text-xs text-slate-400 truncate">{c.escopo === "interna" ? c.labelExibicao + " · " : ""}{c.ultimaMsg || c.labelExibicao}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 ml-1 shrink-0">
                      {c.naoLidas > 0 && <span className="text-[10px] font-bold text-white bg-red-500 rounded-full px-1.5 py-0.5" data-testid={`conv-naolidas-${c.id}`}>{c.naoLidas}</span>}
                      {perfil !== "familia" && (
                        <div className="flex gap-1">
                          <span onClick={(e) => resolver(c, e)} title={c.resolvida ? "Reabrir" : "Marcar resolvida"} data-testid={`conv-resolver-${c.id}`} className="cursor-pointer"><CheckCircle2 className={`w-4 h-4 ${c.resolvida ? "text-emerald-500" : "text-slate-300 hover:text-emerald-500"}`} /></span>
                          <span onClick={(e) => fixar(c, e)} title={c.fixada ? "Desafixar" : "Fixar"} data-testid={`conv-fixar-${c.id}`} className="cursor-pointer"><Star className={`w-4 h-4 ${c.fixada ? "text-amber-500 fill-amber-400" : "text-slate-300 hover:text-amber-500"}`} /></span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingChat;
